import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import Swal from "sweetalert2";
import "../../../assets/css/gcstaff/writeReview/WriteReview.css";
import { Badge } from "react-bootstrap";
import Pagination from "../../../components/general/pagination/Pagination";
import Loader from "../../../components/general/loader/Loader";

const ratingLabels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];

/* ── Star Rating ── */
const StarRating = ({ rating, hovered, onRate, onHover, onLeave }) => (
  <div className="gcStudent-wr-stars" onMouseLeave={onLeave}>
    {[1, 2, 3, 4, 5].map((star) => {
      const filled = star <= (hovered || rating);
      return (
        <button
          key={star}
          type="button"
          className={`gcStudent-wr-star ${filled ? "gcStudent-wr-star--filled" : ""}`}
          onClick={() => onRate(star)}
          onMouseEnter={() => onHover(star)}
          aria-label={`Rate ${star} star`}
        >
          <svg
            viewBox="0 0 24 24"
            fill={filled ? "currentColor" : "none"}
            stroke="currentColor"
            strokeWidth="1.6"
            width="28"
            height="28"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      );
    })}
    {(hovered || rating) > 0 && (
      <span className="gcStudent-wr-rating-label">
        {ratingLabels[hovered || rating]}
      </span>
    )}
  </div>
);

/* ── Display Stars ── */
const DisplayStars = ({ rating }) => (
  <div className="gcStudent-wr-display-stars">
    {[1, 2, 3, 4, 5].map((s) => (
      <svg
        key={s}
        viewBox="0 0 24 24"
        fill={s <= rating ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.6"
        width="16"
        height="16"
        className={
          s <= rating
            ? "gcStudent-wr-display-star--filled"
            : "gcStudent-wr-display-star--empty"
        }
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ))}
  </div>
);

/* ══════════════════════════
   Main Component
   ✅ teacher + token now come from AppContext (loggedInUser, authToken),
   no localStorage reads left in this file
══════════════════════════ */
const ITEMS_PER_PAGE = 5; // review cards text-heavy hain, is liye chhota page size

const TeacherReviewPage = () => {
  const { baseUrl, loggedInUser, authToken } = useContext(AppContext);

  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // DB se reviews
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false); // initial API fetch loading
  const [viewLoading, setViewLoading] = useState(false); // page-switch loading (sirf visible reviews k liye)
  const [currentPage, setCurrentPage] = useState(1); // pagination state

  // Pehli render par view-loading trigger na ho, is liye ref se track kar rahe hain
  const isFirstRender = useRef(true);

  const loadAllReviews = () => {
    if (!loggedInUser?.id) return;

    setReviewsLoading(true);

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/reviews/loadAllReviews`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success) {
          setReviews(res.data.reviews);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setReviewsLoading(false));
  };

  useEffect(() => {
    if (loggedInUser?.id) {
      loadAllReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser]);

  /* ── Pagination slice (sirf current page ki reviews list mein jayengi) ── */
  const paginatedReviews = reviews.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  /* ── Sirf visible reviews ki loading dikhayein:
     - Jab pagination se dusre page par jayen (ya naya review submit hone par page 1 pe reset ho)
     Poori list ek sath "pop" nahi hogi, sirf jitne reviews is waqt
     screen par show hone hain unki hi loading chalegi. ── */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setViewLoading(true);
    const timer = setTimeout(() => setViewLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentPage]);

  // ── submitReview —
  const submitReview = () => {
    if (!rating || !reviewText.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please provide both a rating and review text.",
        confirmButtonColor: "#213b52",
      });
      return;
    }

    if (!loggedInUser?.id) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Your session expired. Please log in again.",
        confirmButtonColor: "#213b52",
      });
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);
    formData.append("rating", rating);
    formData.append("review", reviewText.trim());

    axios
      .post(`${baseUrl}/teacher/reviews/submitReview`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        console.log("log from the submit review response", res.data);
        setSubmitting(false);
        if (res.data.success == true) {
          setRating(0);
          setReviewText("");
          setSubmitted(true);

          setCurrentPage(1); // naya review sabse upar aayega, page 1 pe le aao
          loadAllReviews();
          Swal.fire({
            icon: "success",
            title: "Review Submitted!",
            text: "Your review will appear on the home page after admin approval.",
            confirmButtonColor: "#213b52",
          });
          setTimeout(() => setSubmitted(false), 3000);
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: res.data.message || "Something went wrong.",
            confirmButtonColor: "#213b52",
          });
        }
      })
      .catch((err) => {
        setSubmitting(false);
        console.error(err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong. Please try again.",
          confirmButtonColor: "#213b52",
        });
      });
  };

  return (
    <div className="gcStudent-wr-page">
      {/* Header */}
      <h2 className="gcStudent-wr-heading">Write a Review</h2>
      <p className="gcStudent-wr-subheading">
        Share your experience with our library platform. Your review will appear
        on the public site after admin approval.
      </p>

      {/* Form Card */}
      <div className="gcStudent-wr-card">
        {/* Stars */}
        <div className="gcStudent-wr-field">
          <label className="gcStudent-wr-label">Your Rating</label>
          <StarRating
            rating={rating}
            hovered={hovered}
            onRate={setRating}
            onHover={setHovered}
            onLeave={() => setHovered(0)}
          />
        </div>

        {/* Textarea */}
        <div className="gcStudent-wr-field">
          <label className="gcStudent-wr-label">Your Review</label>
          <textarea
            className="gcStudent-wr-textarea"
            placeholder="Tell us about your experience with the library..."
            rows={5}
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
          />
        </div>

        {/* Submit Button */}
        <button
          className={`gcStudent-wr-submit-btn ${submitted ? "gcStudent-wr-submit-btn--done" : ""}`}
          onClick={submitReview}
          disabled={submitting}
        >
          {submitting ? (
            <span className="gcStudent-wr-spinner" />
          ) : (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="16"
              height="16"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          )}
          {submitting
            ? "Submitting..."
            : submitted
              ? "Submitted!"
              : "Submit Review"}
        </button>
      </div>

      {/* Past Reviews — DB se */}
      <div className="gcStudent-wr-past-section">
        <h3 className="gcStudent-wr-past-heading">Your Past Reviews</h3>

        {/* ── Initial Loading (poori list pehli baar load ho rahi hai) ── */}
        {reviewsLoading && <Loader message="Reviews loading, Please wait..." />}

        {!reviewsLoading && reviews.length === 0 && (
          <p className="gcStudent-wr-no-reviews">No reviews submitted yet.</p>
        )}

        {!reviewsLoading && reviews.length > 0 && (
          <>
            {viewLoading ? (
              /* ── Page-switch loading — sirf is section ki, baaki page affect nahi hota ── */
              <Loader message="Loading reviews..." />
            ) : (
              <div className="gcStudent-wr-past-list">
                {paginatedReviews.map((rev, i) => (
                  <div
                    key={rev.id}
                    className="gcStudent-wr-past-item"
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    <div className="gcStudent-wr-past-top d-flex justify-content-between">

                      {/* Left Side */}
                      <DisplayStars rating={Number(rev.rating)} />

                      {/* Right Side */}
                      <div className="text-end">
                        <div className="gcStudent-wr-past-date">
                          {new Date(rev.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </div>

                        <div className="mt-1">
                          <Badge
                            bg={
                              rev.status === "approved"
                                ? "success"
                                : rev.status === "pending"
                                ? "warning"
                                : "danger"
                            }
                          >
                            {rev.status}
                          </Badge>
                        </div>
                      </div>

                    </div>

                    <p className="gcStudent-wr-past-text">
                      {rev.review}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* ── Pagination (view-loading k dauran bhi visible rehti hai) ── */}
            <Pagination
              currentPage={currentPage}
              totalItems={reviews.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default TeacherReviewPage;